/**
 * Per-studio theme: the installed Astryx theme extended at runtime with the studio's
 * accent (a static SPA, so runtime defineTheme is the documented choice for user branding).
 *
 * Look: frosted glass — translucent fills with a soft diagonal highlight, hairline borders,
 * pill controls. Two moods: black glass (detailing) and light milky glass (beauty). The studio
 * accent stays for small signals (active tab, focus, links); surfaces and buttons are neutral glass.
 */
import { defineTheme, type DefinedTheme } from '@astryxdesign/core/theme';
import { defaultThemeSlug, themes } from '../astryx-themes.ts';

export type ThemeMode = 'light' | 'dark';

const ink = (pct: number) => `color-mix(in srgb, var(--color-text-primary) ${pct}%, transparent)`;
const milk = (pct: number) => `color-mix(in srgb, #ffffff ${pct}%, transparent)`;
const blur = (px: number) => `blur(${px}px) saturate(140%)`;

type Role = 'strong' | 'faint' | 'clear' | 'card' | 'input' | 'float';

/**
 * Glass recipes per mode: [fill, highlight, border, blur].
 * Dark: a faint light tint on black. Light: milky white over a cream page with an ink hairline.
 */
const RECIPES: Record<ThemeMode, Record<Role, [number, number, number, number]>> = {
  dark: {
    strong: [12, 20, 24, 16],
    faint: [5, 12, 14, 16],
    clear: [6, 16, 20, 14],
    card: [4, 9, 11, 16],
    input: [5, 8, 16, 16],
    float: [10, 16, 18, 24],
  },
  light: {
    strong: [80, 100, 10, 16],
    faint: [55, 95, 9, 16],
    clear: [40, 75, 14, 14],
    card: [68, 100, 7, 18],
    input: [78, 100, 12, 16],
    float: [86, 100, 10, 24],
  },
};

function glassFor(mode: ThemeMode) {
  return (role: Role) => {
    const [fill, hi, border, b] = RECIPES[mode][role];
    const dark = mode === 'dark';
    const fillC = dark ? ink(fill) : milk(fill);
    const hiC = dark ? ink(hi) : milk(hi);
    return {
      backgroundColor: fillC,
      backgroundImage: `linear-gradient(120deg, ${hiC}, transparent 55%)`,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: ink(border),
      backdropFilter: blur(b),
      WebkitBackdropFilter: blur(b),
      // Light glass also floats on a soft shadow so white panels read on a cream page.
      boxShadow: dark ? `inset 0 1px 0 ${hiC}` : `inset 0 1px 0 ${hiC}, 0 8px 24px ${ink(5)}`,
    };
  };
}

const memo = new Map<string, DefinedTheme>();

export function tenantTheme(accent: string, mode: ThemeMode = 'dark'): DefinedTheme {
  const key = `${accent.toLowerCase()}-${mode}`;
  const hit = memo.get(key);
  if (hit) return hit;
  const glass = glassFor(mode);
  const dark = mode === 'dark';
  const theme = defineTheme({
    name: `studio-${accent.toLowerCase().slice(1)}-${mode}`,
    extends: themes[defaultThemeSlug],
    color: { accent: [accent, accent], neutralStyle: 'neutral' },
    tokens: {
      // Dark: pure black page, near-black raised surfaces. Light: warm milky cream.
      '--color-background-body': ['#F6F0EB', '#000000'],
      '--color-background-surface': ['#FBF7F3', '#0b0c0f'],
      '--color-background-card': ['#ffffff', '#111318'],
      '--color-background-popover': ['#ffffff', '#15171c'],
      // Softer, larger rounding everywhere; controls are pills.
      '--radius-container': '22px',
      '--radius-element': 'var(--radius-full)',
    },
    components: {
      button: {
        base: { borderRadius: 'var(--radius-full)' },
        // Main actions: brighter glass. Secondary: fainter glass. Ghost stays bare.
        'variant:primary': { ...glass('strong'), color: 'var(--color-text-primary)' },
        'variant:secondary': { ...glass('faint'), color: 'var(--color-text-primary)' },
        // Call to action on top of the hero photo (custom variant): clear pill, label left, arrow right.
        'variant:glass': {
          ...glass('clear'),
          color: 'var(--color-text-primary)',
          justifyContent: 'space-between',
          minHeight: 'calc(var(--spacing-8) + var(--spacing-6))',
          paddingInline: 'var(--spacing-6)',
          fontSize: 'var(--font-size-lg)',
        },
      },
      card: {
        base: { ...glass('card'), borderRadius: 'var(--radius-container)' },
      },
      'banner-frame': {
        base: { borderRadius: 'var(--radius-container)' },
      },
      // Info notes (demo mode, hints) as neutral glass; warnings and errors keep their colors.
      // (The info banner re-points the text tokens to blue; the icon tokens carry the neutral values.)
      banner: {
        'status:info': {
          '--color-text-primary': 'var(--color-icon-primary)',
          '--color-text-secondary': 'var(--color-icon-secondary)',
          '--color-accent': 'var(--color-icon-secondary)',
          ...glass('faint'),
        },
      },
      'bottom-sheet': {
        base: {
          backgroundColor: `color-mix(in srgb, var(--color-background-body) ${dark ? 72 : 84}%, transparent)`,
          backgroundImage: `linear-gradient(160deg, ${dark ? ink(8) : milk(70)}, transparent 40%)`,
          backdropFilter: blur(28),
          WebkitBackdropFilter: blur(28),
          borderTop: `1px solid ${ink(dark ? 16 : 10)}`,
          borderTopLeftRadius: 'var(--radius-page)',
          borderTopRightRadius: 'var(--radius-page)',
        },
      },
      // Time slots and other toggles: glass pills; the picked one turns solid (white on dark, ink on light).
      'toggle-button': {
        base: { ...glass('faint'), borderRadius: 'var(--radius-full)', color: 'var(--color-text-primary)', height: 'auto', minHeight: 'var(--spacing-10)' },
        'isPressed:true': {
          backgroundColor: 'var(--color-text-primary)',
          backgroundImage: 'none',
          color: 'var(--color-background-body)',
          borderColor: 'var(--color-text-primary)',
        },
      },
      'segmented-control': {
        base: { ...glass('faint'), borderRadius: 'var(--radius-full)' },
      },
      'segmented-control-item': {
        base: { borderRadius: 'var(--radius-full)' },
        'selected:true': { backgroundColor: dark ? ink(16) : milk(100), color: 'var(--color-text-primary)' },
      },
      'text-input': {
        base: { ...glass('input'), borderRadius: 'var(--radius-full)' },
      },
      'text-area': {
        base: { ...glass('input'), borderRadius: 'var(--radius-container)' },
      },
      // Assistant chat: glass composer, the visitor's messages in brighter glass.
      'chat-composer': {
        base: { ...glass('input'), borderRadius: 'var(--radius-page)' },
      },
      'chat-message-bubble': {
        'sender:user': { ...glass('strong'), color: 'var(--color-text-primary)' },
        'sender:assistant': { ...glass('faint') },
      },
      toast: {
        base: { ...glass('float'), borderRadius: 'var(--radius-container)' },
      },
      'alert-dialog': {
        base: {
          backgroundColor: `color-mix(in srgb, var(--color-background-body) ${dark ? 70 : 86}%, transparent)`,
          backdropFilter: blur(28),
          WebkitBackdropFilter: blur(28),
          border: `1px solid ${ink(dark ? 16 : 10)}`,
          borderRadius: 'var(--radius-page)',
        },
      },
    },
  });
  memo.set(key, theme);
  return theme;
}

/** Neutral UI before the studio (and its accent) is known — avoids a flash of the wrong colors. */
export const baseTheme = tenantTheme('#8B93A1');
export const baseLightTheme = tenantTheme('#8B93A1', 'light');
